<template>
  <div>
    <div
      v-if="pageLoading"
      style="display: flex; top: 0px; position: fixed; left: 0px; right: 0px; bottom: 0px; background-color: rgba(247, 247, 247, 0.9); z-index: 9999; justify-content: center; align-items: center;"
    >
      <h3>Loading deletion requests...</h3>
    </div>
    <div v-else>
      <div class="row align-items-center justify-content-between mb-2">
        <div class="col">
          <h1 class="mb-2 font-regular">Account deletion requests</h1>
        </div>
        <div class="col-auto">
          <select class="form-control" v-model="status" @change="load(1)">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <CDataTable :items="pageData" :fields="fields" sorter>
            <template #customer="{ item }">
              <td>
                <router-link
                  v-if="item.customer && permissions.includes('view-customer')"
                  :to="{ name: 'CustomerComponent', params: { slug: item.user_id } }"
                >
                  {{ customerName(item) }}
                </router-link>
                <span v-else>{{ customerName(item) }}</span>
              </td>
            </template>
            <template #phone="{ item }">
              <td>{{ item.customer ? item.customer.phone_number : "" }}</td>
            </template>
            <template #reason="{ item }">
              <td>{{ item.reason_label }}</td>
            </template>
            <template #status="{ item }">
              <td>
                <span class="badge" :class="statusClass(item.status)">{{ item.status }}</span>
              </td>
            </template>
            <template #date="{ item }">
              <td>{{ item.created_at | date }}</td>
            </template>
            <template #action="{ item }">
              <td>
                <button class="button" type="button" @click="openRequest(item)">View</button>
              </td>
            </template>
          </CDataTable>
          <CPagination
            :activePage.sync="next_page"
            :pages="total"
            @update:activePage="onPageChange"
          />
        </div>
      </div>
    </div>

    <modal v-show="selected" footer @close="closeRequest">
      <template v-slot:title>
        <h5>{{ selected ? selected.reference : "Request" }}</h5>
      </template>
      <template v-slot:body>
        <div v-if="selected">
          <p><strong>Customer:</strong> {{ customerName(selected) }}</p>
          <p><strong>Phone:</strong> {{ selected.customer ? selected.customer.phone_number : "" }}</p>
          <p><strong>Email:</strong> {{ selected.customer ? selected.customer.email : "" }}</p>
          <p><strong>Reason:</strong> {{ selected.reason_label }}</p>
          <p><strong>Details:</strong></p>
          <p style="white-space: pre-wrap;">{{ selected.details }}</p>
          <p v-if="selected.review_note"><strong>Review note:</strong> {{ selected.review_note }}</p>
          <div v-if="selected.status === 'pending' && canApprove" class="form-group">
            <label for="review_note">Note (optional)</label>
            <textarea
              id="review_note"
              v-model.trim="reviewNote"
              class="form-control"
              rows="3"
              maxlength="1000"
            ></textarea>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <button type="button" class="button mr-2" @click="closeRequest">Close</button>
        <button
          v-if="selected && selected.status === 'pending' && canApprove"
          type="button"
          class="btn btn-secondary mr-2"
          :disabled="actionLoading"
          @click="rejectRequest"
        >
          Reject
        </button>
        <button
          v-if="selected && selected.status === 'pending' && canApprove"
          type="button"
          class="button"
          :disabled="actionLoading"
          @click="showTwoFaPrompt = true"
        >
          Approve
        </button>
      </template>
    </modal>

    <two-factor-prompt
      :visible="showTwoFaPrompt"
      :loading="actionLoading"
      title="Approve account deletion"
      description="Enter your authenticator code to close this customer's account."
      submit-label="Approve deletion"
      loading-text="Approving..."
      @close="showTwoFaPrompt = false"
      @submit="approveRequest"
    />
  </div>
</template>

<script>
import CDataTable from "../../components/CDataTable.vue";
import CPagination from "../../components/CPagination.vue";
import ApiService from "../../services/api.service";
import ErrorService from "../../services/error.service";
import Swal from "sweetalert2";

export default {
  name: "AccountDeletionRequests",
  components: {
    CDataTable,
    CPagination
  },
  data() {
    return {
      pageLoading: true,
      actionLoading: false,
      pageData: [],
      total: 1,
      current: 1,
      next_page: 1,
      status: "pending",
      selected: null,
      reviewNote: "",
      showTwoFaPrompt: false,
      fields: [
        { key: "reference", label: "Reference" },
        { key: "customer", label: "Customer" },
        { key: "phone", label: "Phone" },
        { key: "reason", label: "Reason" },
        { key: "status", label: "Status" },
        { key: "date", label: "Requested" },
        { key: "action", label: "" }
      ]
    };
  },
  computed: {
    permissions() {
      return this.$store.getters.permissions || [];
    },
    canApprove() {
      return this.permissions.includes("approve-account-deletion");
    }
  },
  mounted() {
    this.load(1);
  },
  methods: {
    customerName(item) {
      if (!item.customer) {
        return "Customer";
      }
      return `${item.customer.first_name || ""} ${item.customer.last_name || ""}`.trim();
    },
    statusClass(status) {
      if (status === "approved") return "badge-danger";
      if (status === "rejected") return "badge-secondary";
      return "badge-warning";
    },
    async load(page) {
      try {
        const statusQuery = this.status ? `&status=${this.status}` : "";
        const res = await ApiService.get(`account-deletion/list?page=${page}${statusQuery}`);
        this.pageData = res.data.data.data;
        this.total = res.data.data.last_page || 1;
        this.current = res.data.data.current_page || 1;
        this.next_page = this.current;
      } catch (error) {
        ErrorService.displayErrorAlert(
          (error.response && error.response.data) || "Unable to load deletion requests"
        );
      } finally {
        this.pageLoading = false;
      }
    },
    onPageChange(page) {
      if (page && page !== this.current) {
        this.load(page);
      }
    },
    openRequest(item) {
      this.selected = item;
      this.reviewNote = "";
    },
    closeRequest() {
      if (this.actionLoading) {
        return;
      }
      this.selected = null;
      this.reviewNote = "";
      this.showTwoFaPrompt = false;
    },
    async approveRequest(code) {
      if (!this.selected) {
        return;
      }
      this.actionLoading = true;
      try {
        const res = await ApiService.post("account-deletion/approve", {
          reference: this.selected.reference,
          code,
          review_note: this.reviewNote || null
        });
        this.showTwoFaPrompt = false;
        this.selected = null;
        Swal.fire({
          title: "Approved",
          text: res.data.message,
          icon: "success",
          timer: 3000,
          showConfirmButton: false
        });
        this.load(this.current || 1);
      } catch (error) {
        ErrorService.displayErrorAlert(
          (error.response && error.response.data) || "Unable to approve this request"
        );
      } finally {
        this.actionLoading = false;
      }
    },
    async rejectRequest() {
      if (!this.selected) {
        return;
      }
      this.actionLoading = true;
      try {
        const res = await ApiService.post("account-deletion/reject", {
          reference: this.selected.reference,
          review_note: this.reviewNote || null
        });
        this.selected = null;
        Swal.fire({
          title: "Rejected",
          text: res.data.message,
          icon: "success",
          timer: 3000,
          showConfirmButton: false
        });
        this.load(this.current || 1);
      } catch (error) {
        ErrorService.displayErrorAlert(
          (error.response && error.response.data) || "Unable to reject this request"
        );
      } finally {
        this.actionLoading = false;
      }
    }
  }
};
</script>
